import { useState, type FormEvent } from "react";
import { GUVENILIR_CIHAZ_GUN, SIFRE_SIFIRLAMA_DAKIKA } from "@koclukpanel/contracts";
import { Alan, Buton, GirisKabugu, marka, OnayKutusu } from "@koclukpanel/ui";
import { AgHatasi, ApiHatasi, girisYap, ikinciAsama, sifirlamaIste } from "./api";
import { girisSebebiCumlesi, type GirisSebebi } from "./giris-sebebi";

interface GirisProps {
  /**
   * `kalanYedekKod` YALNIZCA yedek kodla girildiginde dolu (G-AUT-02a);
   * normal 2FA kodunda ve guvenilir cihazda `null`. Ekran bunu kendisi
   * cikaramaz — sunucudan gelmezse gosterecek bir sey de yok.
   *
   * Once bu yanit TAMAMEN atiliyordu (`onGirdi: () => void`): telefonunu
   * kaybedip yedek koduyla giren ogrenci kac kodu kaldigini ancak
   * Hesabim -> Guvenlik'e giderse ogreniyordu.
   */
  onGirdi: (kalanYedekKod: number | null) => void;
  /**
   * Buraya NEDEN donuldu — cikis mi, oturum mu bitti.
   *
   * Uc yoldan da sessizce bu ekrana donuluyordu; ogrenci kendini
   * birden giris formunda buluyordu. Gerekce `giris-sebebi.ts`te.
   */
  sebep?: GirisSebebi | null;
}

/**
 * OGRENCI GIRIS EKRANI
 *
 * Panelinkiyle ayni akis (iki asamali, ayni hata mesaji politikasi) ama
 * MOBIL ONCELIKLI: alanlar tam genislik, dugmeler 44px, form ekranin
 * ustune degil ORTASINA hizali — telefon tutan elin basparmagi ekranin
 * alt yarisina rahat ulasir.
 *
 * reCAPTCHA burada YOK. Ogrenci uygulamasi gunde defalarca aciliyor ve
 * kullanicilarin cogu resit degil; her acilista ucuncu tarafa istek
 * gondermek gereksiz. Bot riski asil panelde (personel hesaplari).
 *
 * ================================================================
 * BU EKRAN ARTIK ORTAK — "SEN" DEĞİL "SİZ" DER
 * ================================================================
 * Veli 05.09.2026'da bu uygulamaya taşındı (C55). Giriş ekranı,
 * kimlik daha bilinmeden çizilen tek ekran: karşısındakinin bir
 * çocuk mu yoksa bir veli mi olduğunu **bilemez**.
 *
 * Bilinmezlikte yanlış olmayan taraf seçiliyor. "Siz" bir öğrenciye
 * yalnızca biraz resmî gelir; "sen" ise tanımadığı bir yetişkine
 * Türkçede doğrudan bir nezaketsizliktir. Zarar simetrik değil.
 *
 * Bu kural giriş sonrası ekranları BAĞLAMIYOR: `HaftalikProgram`,
 * `GorevKarti`, `HataDefterim` gibi yalnızca öğrencinin gördüğü
 * yerler "sen" demeye devam ediyor ve etmeli.
 *
 * Kuralın bağladığı dosyalar (kimlik bilinmeden ya da iki yüzeyde
 * birden çizilenler):
 *
 *     Giris.tsx · giris-sebebi.ts · yedek-kod-uyarisi.ts
 *
 * Metinler `giris-sebebi.test.ts` ve `yedek-kod-uyarisi.test.ts`
 * içinde harfiyen sabitlendi — biri "sen"e geri çevirirse test düşer.
 */
export function Giris({ onGirdi, sebep = null }: GirisProps) {
  const sebepCumlesi = girisSebebiCumlesi(sebep);
  const [asama, setAsama] = useState<"sifre" | "kod" | "sifirlama">("sifre");
  /**
   * Sunucunun sifirlama yanitindaki metin.
   *
   * KENDI CUMLEMIZI UYDURMUYORUZ (G-AUT-06): sunucu adres kayitli olsun
   * olmasin AYNI seyi soyluyor. "Gonderildi" yazsaydik kayitli olmayan
   * bir adres giren ogrenci gelmeyecek bir postayi beklerdi;
   * "bulunamadi" yazsaydik sunucunun ozenle sakladigi bilgiyi ekrandan
   * sizdirirdik.
   */
  const [sifirlamaMesaji, setSifirlamaMesaji] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sifre, setSifre] = useState("");
  const [kod, setKod] = useState("");
  const [jeton, setJeton] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [calisiyor, setCalisiyor] = useState(false);
  // G-AUT-01c — varsayilan ISARETSIZ: guvenlik gevsetmesi acik tercih olmali.
  const [cihaziHatirla, setCihaziHatirla] = useState(false);

  function hatayiYaz(err: unknown, varsayilan: string): void {
    if (err instanceof AgHatasi) {
      // Ag hatasi ile kimlik hatasini AYIRIYORUZ: "sifren yanlis" demek
      // internetsiz bir ogrenciyi sifresini degistirmeye iterdi.
      setHata("İnternet bağlantısı yok. Bağlanınca tekrar deneyin.");
      return;
    }
    setHata(err instanceof ApiHatasi ? err.message : varsayilan);
  }

  async function sifreGonder(e: FormEvent): Promise<void> {
    e.preventDefault();
    setHata(null);
    setCalisiyor(true);
    try {
      const sonuc = await girisYap(email.trim(), sifre);
      if (sonuc.ikinciAsama) {
        setJeton(sonuc.asamaJetonu);
        setAsama("kod");
        setSifre("");
      } else {
        onGirdi(null);
      }
    } catch (err) {
      hatayiYaz(err, "Giriş yapılamadı.");
    } finally {
      setCalisiyor(false);
    }
  }

  async function sifirlamaGonder(e: FormEvent): Promise<void> {
    e.preventDefault();
    setHata(null);
    setCalisiyor(true);
    try {
      const { mesaj } = await sifirlamaIste(email.trim());
      setSifirlamaMesaji(mesaj);
    } catch (err) {
      hatayiYaz(err, "İstek gönderilemedi.");
    } finally {
      setCalisiyor(false);
    }
  }

  async function kodGonder(e: FormEvent): Promise<void> {
    e.preventDefault();
    setHata(null);
    setCalisiyor(true);
    try {
      const sonuc = await ikinciAsama(jeton, kod.trim(), cihaziHatirla);
      onGirdi(sonuc.kalanYedekKod);
    } catch (err) {
      hatayiYaz(err, "Kod doğrulanamadı.");
      setKod("");
      if (err instanceof ApiHatasi && err.kod === "BILET_GECERSIZ") {
        setAsama("sifre");
        setJeton("");
      }
    } finally {
      setCalisiyor(false);
    }
  }

  return (
    /*
      ================================================================
      PANELİN KABUĞU — ARTIK BURADA DA (07.09.2026)
      ================================================================
      Önce bu ekranın kendi kartı vardı ve gerekçesi şuydu: panelin
      kabuğu iki sütunlu, 390px'te sol yarısı yok. Gerekçe doğruydu
      ama sonucu telefonda **bomboş beyaz bir sayfada ortada duran bir
      kutu**ydu.

      Emre'nin kararı (07.09.2026): *"arka plan beyaz ortada login
      kutusu istemiyorum, personel girişiyle aynı olsun."*

      Kabuk dar ekran için düzeltildi: ızgaranın yerini aynı gradyanı,
      aynı marka bloğunu ve aynı tamamlama çubuğunu taşıyan yatay bir
      şerit aldı (`GirisKabugu` > `HaftaSeridi`). Artık iki giriş
      ekranı TEK yerden çiziliyor — biri güncellenip öteki eskiyemez.
    */
    <GirisKabugu
      baslik={marka.kisaAd}
      aciklama={
        asama === "sifre"
          ? "Programını görmek için giriş yap."
          : asama === "kod"
            ? "Doğrulama kodunu gir."
            : "Şifreni sıfırlayalım."
      }
    >
      {/*
          SEBEP, HATANIN USTUNDE VE `status` — `alert` DEGIL.

          Cikis yapmak ya da oturumun kapanmasi bir ARIZA degil; kirmizi
          bir uyari kutusu ogrenciye yanlis bir sey yaptigini
          dusundururdu. Yalnizca `asama === "sifre"` iken ciziliyor:
          kod adimindayken sebep artik gecmis, yeri de yok.
        */}
      {sebepCumlesi && asama === "sifre" && (
        <p role="status" className="rounded-alan bg-notr-100 text-notr-700 mb-4 px-3 py-2 text-sm">
          {sebepCumlesi}
        </p>
      )}

      {hata && (
        <div
          role="alert"
          className="rounded-alan bg-hata-500/10 text-hata-700 mb-4 flex items-start gap-2 px-3 py-3 text-sm"
        >
          <span aria-hidden="true">⚠</span>
          <span>{hata}</span>
        </div>
      )}

      {asama === "sifre" ? (
        <form onSubmit={(e) => void sifreGonder(e)} className="flex flex-col gap-5">
          <Alan
            etiket="E-posta"
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Alan
            etiket="Şifre"
            type="password"
            autoComplete="current-password"
            required
            value={sifre}
            onChange={(e) => setSifre(e.target.value)}
          />
          <Buton type="submit" boyut="genis" yukleniyor={calisiyor}>
            Giriş yap
          </Buton>
          {/*
              ================================================================
              ŞİFREMİ UNUTTUM — ÖĞRENCİDE HİÇ YOKTU
              ================================================================
              Sunucu ucu baştan beri vardı ve `@HerkeseAcik()`; panelde
              çalışıyordu, öğrencide çağıran yoktu. Şifresini unutan
              öğrencinin tek yolu koçuna söylemekti ve bu da hiçbir
              ekranda yazmıyordu — eksik özellik değil, çıkmaz sokak.
            */}
          <Buton
            gorunum="sessiz"
            onClick={() => {
              setAsama("sifirlama");
              setSifre("");
              setHata(null);
              setSifirlamaMesaji(null);
            }}
          >
            Şifremi unuttum
          </Buton>
        </form>
      ) : asama === "sifirlama" ? (
        <form onSubmit={(e) => void sifirlamaGonder(e)} className="flex flex-col gap-5">
          {sifirlamaMesaji !== null ? (
            <>
              {/*
                  SUNUCUNUN CÜMLESİ AYNEN. Adres kayıtlı olsun olmasın
                  aynı yanıt dönüyor (G-AUT-06) — arayüz kendi cümlesini
                  uydurursa ya yalan söyler ya da sunucunun sakladığını
                  sızdırır.
                */}
              <p
                role="status"
                className="rounded-alan bg-onay-600/10 text-notr-800 px-3 py-3 text-sm"
              >
                {sifirlamaMesaji}
              </p>
              <p className="text-notr-600 text-sm">
                Bağlantı <strong>{SIFRE_SIFIRLAMA_DAKIKA} dakika</strong> geçerli ve bir kez
                kullanılabilir. Gelmezse önce gereksiz posta klasörünü kontrol edin; yine yoksa{" "}
                {marka.kisaAd} ile iletişime geçin.
              </p>
            </>
          ) : (
            <>
              <Alan
                etiket="E-posta"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                yardim="Şifre belirleme bağlantısını e-posta ile göndereceğiz."
              />
              <Buton type="submit" boyut="genis" yukleniyor={calisiyor}>
                Bağlantı gönder
              </Buton>
            </>
          )}
          <Buton
            gorunum="sessiz"
            onClick={() => {
              setAsama("sifre");
              setHata(null);
              setSifirlamaMesaji(null);
            }}
          >
            Giriş ekranına dön
          </Buton>
        </form>
      ) : (
        <form onSubmit={(e) => void kodGonder(e)} className="flex flex-col gap-5">
          <Alan
            etiket="Doğrulama kodu"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={kod}
            onChange={(e) => setKod(e.target.value)}
            yardim="Doğrulama uygulamanızdaki 6 haneli kod ya da yedek kodlarınızdan biri."
          />
          {/*
              ================================================================
              "SMS NEREDE" SORUSU SORULMADAN CEVAPLANIYOR
              ================================================================
              `GirisDogrulama.dc.html` bu cumleyi acikca istiyor ve panel
              girisinde VAR (`apps/panel/src/Giris.tsx` altNot). Ogrenci
              girisinde HIC yoktu — olculdu 05.09.2026, "SMS" kelimesi
              butun ogrenci ekranlarinda yalnizca bildirim tercihlerinde
              geciyordu.

              Bedeli sessiz: kod bekleyen ogrenci gelmeyen bir SMS'i
              bekler, sistemi bozuk sanip vazgecer ve bunu hicbir olcum
              gostermez. Sebebi de yaziyor — "gondermiyoruz" tek basina
              keyfi bir kisitlama gibi okunurdu.
            */}
          <p className="text-notr-600 text-xs">
            Kod <strong className="text-notr-700">SMS ile gönderilmez</strong>. SMS, SIM değiştirme
            saldırısına açık; doğrulama uygulaması cihazdan çıkmaz.
          </p>
          {/*
              G-AUT-01c — "Bu cihazi 30 gun hatirla".

              OGRENCIDE DE VAR: 2FA istege bagli ama acan ogrenci de
              telefonunda her giriste kod yazmak zorunda kalmasin.
              Varsayilan isaretsiz (kural 9).
            */}
          <OnayKutusu
            checked={cihaziHatirla}
            onChange={(e) => setCihaziHatirla(e.target.checked)}
            etiket={`Bu cihazı ${GUVENILIR_CIHAZ_GUN} gün hatırla`}
            yardim="Ortak bir bilgisayardaysanız işaretlemeyin."
          />
          <Buton type="submit" boyut="genis" yukleniyor={calisiyor}>
            Doğrula
          </Buton>
          <Buton
            gorunum="sessiz"
            onClick={() => {
              setAsama("sifre");
              setJeton("");
              setKod("");
              setHata(null);
            }}
          >
            Baştan başla
          </Buton>
        </form>
      )}
    </GirisKabugu>
  );
}
